import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setCurrentCompany } from 'reducers/auth';
// api
import { getTenantSettings, updateTenantSettings } from 'endpoints/tenantSettings';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import pick from 'lodash.pick';
import { formatBackEndWeightToSystem } from 'utils/weightHelper';
import { showToastrMessage } from 'utils';
import { isSchedulingTariffUser } from 'utils/userHelper';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
// components
import Preloader from 'components/Preloader';
import { FormattedMessage } from 'react-intl';
import Subnavigation from 'components/Subnavigation';
// styles
import './Preferences.scss';

const Preferences = ({ children, setCurrentCompany, isSchedulingAccessOnly, router, location, countryCode }) => {
  const [tenant_settings, setTenantSettings] = useState({
    settings: {
      measurement_system: 'imperial',
      temperature_system: 'fahrenheit',
      sms_compliance: true,
      inventory_reconciliation_active: true,
      inventory_reconciliation: 'mon',
      auto_report_emails: false,
      auto_report_period: 'weekly',
      auto_report_mortality_percentage: false,
      use_group_start_baseline_for_mortality_calculations: false,
      withdrawal_tracking_on: false,
      comment_company_direct_post: false,
      trucking_admins_farmfeed: false,
      withdrawal_weight_limit: 200,
      new_withdrawal_weight_limit: 200,
      track_mortality_reasons: false,
      company_withdrawal_warning_recipients: [],
      water_consumption_monitoring: false,
      water_consumption_drop_limit: 0,
      barnsheets_limited_access: false,
      surveys: [{}],
      scheduling_access: true,
      media_upload: true,
      treatment_compliance: false,
      treatment_compliance_days: 14,
      new_treatment_compliance_days: 14,
      symptom_prompt: false,
      symptom_prompt_days: 3,
      new_symptom_prompt_days: 3,
      abf_tracking: false,
      feed_orders_tracking: false,
      generate_farmfeed_event_on_feed_order_created: false,
      feed_default_delivery_date: false,
      feed_default_delivery_cutoff_time: '13:00',
      new_feed_default_delivery_cutoff_time: '13:00',
      feed_delivery_day_delays: [1, 1, 1, 1, 1, 1, 1],
      new_feed_delivery_day_delays: [1, 1, 1, 1, 1, 1, 1],
      water_usage_farm_users: false,
      water_usage_vets_alerts: true,
      water_usage_notes_notifications_off: false,
      temperature_notes_notifications_off: false,
      veterinary_weekly_summary_emails: false,
      company_veterinary_weekly_summary_users: [],
      sales_insights_weekly_emails: false,
      sales_insights_weekly_day_of_week: null,
      sales_insights_weekly_hour: null,
      sales_insights_weekly_utc_offset: null,
      company_sales_insights_weekly_users: [],
    },
    isLoading: false,
    isLoaded: false,
  });
  const [waterPercents, setPercent] = useState();

  const navLinks = [
    {
      link: '/admin/preferences/settings',
      label: <FormattedMessage id="general.settings" />,
      isHidden: isSchedulingAccessOnly,
    },
    {
      link: '/admin/preferences/daily-checkup',
      label: <FormattedMessage id="general.dailyCheckup" />,
      isHidden: isSchedulingAccessOnly,
    },
    {
      link: '/admin/preferences/integrations',
      label: <FormattedMessage id="general.integrations" />,
    },
  ];

  const setSettings = (data) => {
    const settings = pick(data, ['sms_compliance', 'inventory_reconciliation', 'auto_report_emails',
      'auto_report_period', 'withdrawal_tracking_on', 'withdrawal_weight_limit', 'track_mortality_reasons',
      'comment_company_direct_post', 'trucking_admins_farmfeed', 'temperature_system',
      'company_withdrawal_warning_recipients',
      'measurement_system', 'surveys', 'custom_survey', 'track_water_usage', 'track_temperature',
      'water_consumption_monitoring', 'water_consumption_drop_limit', 'map_centralize', 'map_lat', 'map_lng',
      'map_zoom', 'treatment_protocols', 'treatment_protocol_contacts',
      'barnsheets_limited_access', 'farm_compliance_hour', 'farm_compliance_utc_offset',
      'farm_compliance_second_hour', 'farm_compliance_second_utc_offset',
      'company_analytics_users', 'company_svrs_disabled_users', 'analytics_extended_access', 'svrs_limited_access',
      'auto_report_mortality_percentage', 'treatment_protocols_pg_compliance',
      'multi_placement_treatment_protocols', 'svrs_compliance_emails',
      'use_group_start_baseline_for_mortality_calculations',
      'farms_compliance_emails', 'media_upload', 'general_water_usage_warnings', 'general_withdrawal_warnings',
      'dc_migrations_limited_access', 'svrs_daily_summary_emails', 'veterinary_weekly_summary_emails',
      'svrs_summary_emails', 'dc_mortality_media_prompt',
      'company_veterinary_weekly_summary_users',
      'sales_insights_weekly_emails', 'sales_insights_weekly_day_of_week',
      'sales_insights_weekly_hour', 'sales_insights_weekly_utc_offset',
      'company_sales_insights_weekly_users',
      'treatment_compliance', 'treatment_compliance_days', 'symptom_prompt', 'symptom_prompt_days', 'abf_tracking',
      'closeout_emails', 'company_closeout_email_users',
      'group_closeout_emails', 'company_group_closeout_email_users',
      'manager_productivity_emails', 'company_manager_productivity_email_users',
      'feed_orders_tracking', 'water_usage_farm_users', 'water_usage_vets_alerts',
      'edit_mode_mentions', 'water_usage_notes_notifications_off', 'temperature_notes_notifications_off',
      'require_water_usage', 'require_temperature', 'temperature_setpoint_source',
      'generate_farmfeed_event_on_feed_order_created',
      'feed_default_delivery_date', 'feed_default_delivery_cutoff_time', 'feed_delivery_day_delays',
      // mortality trend settings
      'mortality_trend_alerts',
      'mortality_trend_2_enabled', 'mortality_trend_3_enabled', 'mortality_trend_5_enabled',
      'mortality_trend_7_enabled',
      'mortality_trend_2_threshold_percent',
      'mortality_trend_3_threshold_percent',
      'mortality_trend_5_threshold_percent',
      'mortality_trend_7_threshold_percent',
      'mortality_trend_2_hard_count', 'mortality_trend_3_hard_count',
      'mortality_trend_5_hard_count', 'mortality_trend_7_hard_count',
      'mortality_trend_2_acute_enabled', 'mortality_trend_2_chronic_enabled', 'mortality_trend_2_euthanasia_enabled',
      'mortality_trend_2_treated_enabled',
      'mortality_trend_3_acute_enabled', 'mortality_trend_3_chronic_enabled',
      'mortality_trend_3_euthanasia_enabled', 'mortality_trend_3_treated_enabled',
      'mortality_trend_5_acute_enabled', 'mortality_trend_5_chronic_enabled',
      'mortality_trend_5_euthanasia_enabled', 'mortality_trend_5_treated_enabled',
      'mortality_trend_7_acute_enabled', 'mortality_trend_7_chronic_enabled',
      'mortality_trend_7_euthanasia_enabled', 'mortality_trend_7_treated_enabled',
      'mortality_trend_2_acute_limit', 'mortality_trend_2_chronic_limit', 'mortality_trend_2_euthanasia_limit',
      'mortality_trend_2_treated_limit',
      'mortality_trend_3_acute_limit', 'mortality_trend_3_chronic_limit',
      'mortality_trend_3_euthanasia_limit', 'mortality_trend_3_treated_limit',
      'mortality_trend_5_acute_limit', 'mortality_trend_5_chronic_limit',
      'mortality_trend_5_euthanasia_limit', 'mortality_trend_5_treated_limit',
      'mortality_trend_7_acute_limit', 'mortality_trend_7_chronic_limit',
      'mortality_trend_7_euthanasia_limit', 'mortality_trend_7_treated_limit'
    ]);
    settings.inventory_reconciliation_active = settings.inventory_reconciliation !== 'disabled';
    settings.new_treatment_compliance_days = settings.treatment_compliance_days;
    settings.new_symptom_prompt_days = settings.symptom_prompt_days;
    settings.new_feed_default_delivery_cutoff_time = settings.feed_default_delivery_cutoff_time;
    settings.new_feed_delivery_day_delays = settings.feed_delivery_day_delays;
    settings.new_withdrawal_weight_limit = formatBackEndWeightToSystem(
      settings.withdrawal_weight_limit,
      data.measurement_system
    );
    // Initialize "new_*" fields for mortality trend editable values to track unsaved changes
    const daysList = [2, 3, 5, 7];
    const deathTypes = ['acute', 'chronic', 'euthanasia', 'treated'];
    daysList.forEach((d) => {
      const thresholdKey = `mortality_trend_${d}_threshold_percent`;
      settings[`new_${thresholdKey}`] = settings[thresholdKey];
      const hardCountKey = `mortality_trend_${d}_hard_count`;
      settings[`new_${hardCountKey}`] = settings[hardCountKey];
      deathTypes.forEach((type) => {
        const limitKey = `mortality_trend_${d}_${type}_limit`;
        settings[`new_${limitKey}`] = settings[limitKey];
      });
    });
    if (!settings.inventory_reconciliation_active) settings.inventory_reconciliation = 'mon';
    setCurrentCompany({
      auto_report_period: data.auto_report_period,
      auto_report_mortality_percentage: data.auto_report_mortality_percentage,
      withdrawal_tracking_on: data.withdrawal_tracking_on,
      track_mortality_reasons: data.track_mortality_reasons,
      comment_company_direct_post: data.comment_company_direct_post,
      temperature_system: data.temperature_system,
      measurement_system: data.measurement_system,
      track_water_usage: data.track_water_usage,
      track_temperature: data.track_temperature,
      map_centralize: data.map_centralize,
      map_lat: data.map_lat,
      map_lng: data.map_lng,
      map_zoom: data.map_zoom,
      treatment_protocols: data.treatment_protocols,
      barnsheets_limited_access: data.barnsheets_limited_access,
      farm_compliance_hour: data.farm_compliance_hour,
      farm_compliance_utc_offset: data.farm_compliance_utc_offset,
      company_svrs_disabled_users: data.company_svrs_disabled_users,
      company_analytics_users: data.company_analytics_users,
      treatment_protocols_pg_compliance: data.treatment_protocols_pg_compliance,
      multi_placement_treatment_protocols: data.multi_placement_treatment_protocols,
      svrs_compliance_emails: data.svrs_compliance_emails,
      farms_compliance_emails: data.farms_compliance_emails,
      media_upload: data.media_upload,
      general_water_usage_warnings: data.general_water_usage_warnings,
      general_withdrawal_warnings: data.general_withdrawal_warnings,
      dc_migrations_limited_access: data.dc_migrations_limited_access,
      treatment_compliance: data.treatment_compliance,
      treatment_compliance_days: data.treatment_compliance_days,
      symptom_prompt: data.symptom_prompt,
      symptom_prompt_days: data.symptom_prompt_days,
      abf_tracking: data.abf_tracking,
      company_closeout_email_users: data.company_closeout_email_users,
      require_water_usage: data.require_water_usage,
      require_temperature: data.require_temperature,
      feed_orders_tracking: data.feed_orders_tracking,
      feed_default_delivery_date: data.feed_default_delivery_date,
      feed_default_delivery_cutoff_time: data.feed_default_delivery_cutoff_time,
      feed_delivery_day_delays: data.feed_delivery_day_delays,
      edit_mode_mentions: data.edit_mode_mentions,
      temperature_setpoint_source: data.temperature_setpoint_source,
    });

    setTenantSettings({
      settings: {
        ...settings,
        surveys: settings.surveys ?
          settings.surveys.map((survey) => ({
            ...survey,
            start_date: formatDateForUser(survey.start_date, countryCode),
            end_date: survey.end_date ? formatDateForUser(survey.end_date, countryCode) : null,
          })) :
          [],
        withdrawal_weight_limit: formatBackEndWeightToSystem(settings.withdrawal_weight_limit, data.measurement_system),
      },
      isLoaded: true,
      isLoading: false,
    });

    setPercent(settings.water_consumption_drop_limit ?? 10);
  };

  useEffect(() => {
    const redirectPath = '/admin/preferences/integrations';
    if (isSchedulingAccessOnly && location.pathname !== redirectPath) router.push(redirectPath);

    setTenantSettings({ ...tenant_settings, isLoading: true });
    getTenantSettings()
      .then((data) => {
        setSettings(data);
      });
  }, []);

  const handleSetSettings = (data) => {
    setTenantSettings({
      ...tenant_settings,
      settings: {
        ...tenant_settings.settings,
        ...data,
      }
    });
  };

  const updateSettings = (resource) => {
    setTenantSettings({ ...tenant_settings, isLoading: true });
    updateTenantSettings(resource)
      .then((data) => {
        const [resourceKey] = Object.keys(resource);
        if (resourceKey === 'water_consumption_drop_limit') {
          showToastrMessage('component.setting.waterConsumptionMonitoringSaved');
        }
        setSettings(data);
      })
      .catch((errors) => {
        setTenantSettings({ ...tenant_settings, isLoading: false });
        return toastResponseErrors(errors);
      });
  };

  const childrenWithProps = React.Children.map(children, (child) => child && React.cloneElement(child,
    {
      tenant_settings: tenant_settings.settings,
      temperature_setpoint_source: tenant_settings.settings.temperature_setpoint_source,
      waterPercents,
      updateWaterPercent: setPercent,
      updateSettings,
      setSettings: handleSetSettings,
    }));

  return (
    <div className="Preferences">
      <Subnavigation links={navLinks} className="small-12 column" />
      <section className="small-12 column">
        <Preloader isActive={tenant_settings.isLoading} />
        {tenant_settings.isLoaded && childrenWithProps}
      </section>
    </div>
  );
};

Preferences.propTypes = {
  children: T.node,
  setCurrentCompany: T.func.isRequired,
  isSchedulingAccessOnly: T.bool,
  router: T.object.isRequired,
  location: T.object.isRequired,
  countryCode: T.string,
};

export default connect((state) => ({
  isSchedulingAccessOnly: isSchedulingTariffUser(state.auth.user),
  countryCode: getCurrentCountryCode(state),
}), { setCurrentCompany })(Preferences);
